import { useEffect, useState } from "react";
import { api, fileToBase64 } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { ShareTwinButton } from "@/components/ShareCard";

export default function Avatar() {
  const { t } = useLang();
  const { user, can } = useAuth();
  const [selectedShare, setSelectedShare] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [variant, setVariant] = useState("physical_ideal");
  const [loading, setLoading] = useState(false);
  const [avatars, setAvatars] = useState([]);

  const load = () => api.get("/avatar").then((r) => setAvatars(r.data));
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) return toast.error("Max 8MB");
    const b64 = await fileToBase64(f);
    setPhoto(b64);
  };

  const generate = async () => {
    if (!photo) return toast.error("Upload a photo first");
    setLoading(true);
    try {
      await api.post("/avatar/generate", { photo_base64: photo, variant });
      toast.success("Avatar generated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const variants = [
    { id: "physical_ideal", label: t.avatar.variantIdeal },
    { id: "kinetic_pose", label: t.avatar.variantKinetic },
    { id: "stadium_portrait", label: t.avatar.variantStadium },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="avatar-page">
      <div className="text-[10px] font-mono-tech tracking-[0.3em] text-emerald-400">STUDIO · NANO BANANA</div>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl mt-2" data-testid="avatar-title">{t.avatar.title}</h1>
      <p className="text-slate-400 mt-2 max-w-2xl">{t.avatar.subtitle}</p>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-6">
          <label className="block cursor-pointer">
            <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="avatar-file-input" />
            <div className="aspect-square rounded-lg border-2 border-dashed border-[#1E2638] hover:border-[#00F076]/60 transition-colors flex items-center justify-center overflow-hidden">
              {photo ? (
                <img src={photo} alt="preview" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center text-slate-500">
                  <Upload className="w-8 h-8 mx-auto mb-3" />
                  <div className="font-mono-tech text-xs tracking-wider uppercase">{t.avatar.upload}</div>
                </div>
              )}
            </div>
          </label>
          <div className="mt-4 space-y-2">
            <div className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">Style</div>
            <div className="grid grid-cols-3 gap-2">
              {variants.map((v) => (
                <button
                  key={v.id}
                  onClick={() => setVariant(v.id)}
                  data-testid={`variant-${v.id}`}
                  className={`px-3 py-2 rounded-md text-xs font-semibold uppercase tracking-wide border transition-all ${
                    variant === v.id ? "bg-[#00F076] text-black border-[#00F076]" : "bg-[#090C10] border-[#1E2638] text-slate-300 hover:border-[#00F076]/50"
                  }`}
                >{v.label}</button>
              ))}
            </div>
          </div>
          <Button
            onClick={generate}
            disabled={loading || !photo}
            data-testid="avatar-generate-btn"
            className="mt-6 w-full bg-[#00F076] hover:bg-[#00C963] text-black font-bold uppercase tracking-wider"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {loading ? t.avatar.generating : t.avatar.generate}
          </Button>
        </div>

        <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-6">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div className="font-display font-bold text-xl uppercase">{t.avatar.gallery}</div>
            {can("share") && <ShareTwinButton size="sm" avatarSrc={(avatars.find((a) => a.id === selectedShare) || avatars[0])?.image_data_url} name={user?.full_name} position={user?.position} />}
          </div>
          {avatars.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-slate-500 text-sm">—</div>
          ) : (
            <div className="grid grid-cols-2 gap-3 max-h-[540px] overflow-auto">
              {avatars.map((a) => (
                <div key={a.id} onClick={() => setSelectedShare(a.id)} className={`relative rounded-lg overflow-hidden border cursor-pointer transition-colors ${(selectedShare || avatars[0]?.id) === a.id ? "border-[#00F076]" : "border-[#1E2638]"}`} data-testid={`avatar-item-${a.id}`}>
                  <img src={a.image_data_url} alt={a.variant} className="w-full aspect-square object-cover" />
                  <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-[#090C10]/80 text-[10px] font-mono-tech tracking-wider uppercase">{a.variant.replace("_", " ")}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
